import { apiClient } from './apiClient';
import { Document } from '../slices/documentsSlice';

// Pipeline types for upload
export type UploadPipeline = 'knowledge' | 'intelligence';

// Upload request interfaces
export interface UploadDocumentRequest {
  filename: string;
  content_type?: string;
  file_size?: number;
  kind?: string;
  doc_type?: string;
  category?: string;
  title?: string;
  meta?: Record<string, unknown>;
  /** One pipeline per document */
  pipelines?: UploadPipeline[];
}

export interface UploadDocumentResult {
  filename: string;
  document_id: string | null;
  upload_url: string | null;
  storage_path: string | null;
  method?: string;
  fields?: Record<string, unknown>;
  success: boolean;
  error: string | null;
}

export interface UploadResponse {
  results: UploadDocumentResult[];
}

export interface UploadCallbackRequest {
  document_id: string;
  storage_path: string;
  size: number;
  content_type: string;
}

// Progress event interface
export interface ProgressEvent {
  document_id: string;
  status: string;
  progress: number;
  stage?: string;
  error_message?: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
  indeterminate?: boolean;
}

// Bulk upload interfaces
export interface BulkUploadDocumentRequest {
  filename: string;
  content_type?: string;
  file_size?: number;
  kind?: string;
  doc_type?: string;
  category?: string;
  title?: string;
  pipeline?: UploadPipeline;
  pipelines?: UploadPipeline[];
  meta?: Record<string, unknown>;
}

export interface BulkUploadDocumentResult {
  filename: string;
  document_id: string | null;
  upload_url: string | null;
  storage_path: string | null;
  method?: string;
  fields?: Record<string, unknown>;
  success: boolean;
  error: string | null;
}

export interface BulkUploadResponse {
  total: number;
  successful: number;
  failed: number;
  results: BulkUploadDocumentResult[];
}

// Bulk upload constants
export const BULK_UPLOAD_LIMITS = {
  MAX_FILES: 100,
  MAX_FILE_SIZE_MB: 500,
  MAX_TOTAL_SIZE_MB: 500,
  CONCURRENT_UPLOADS: 5,
} as const;

export const documentsApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    getDocuments: builder.query<Document[], { siteId?: string } | void>({
      query: (params) => {
        const queryParams = params?.siteId ? `?site_id=${params.siteId}` : '';
        return {
          url: `/api/v1/documents/${queryParams}`,
          method: 'GET',
        };
      },
    }),
    deleteDocument: builder.mutation<void, string>({
      query: (documentId) => ({
        url: `/api/v1/documents/${documentId}`,
        method: 'DELETE',
      }),
    }),
    uploadDocument: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/api/v1/documents/upload',
        method: 'POST',
        body: formData,
      }),
    }),
    // New upload endpoint - request upload URL
    requestUpload: builder.mutation<UploadResponse, { tenant_id: string; documents: UploadDocumentRequest[] }>({
      query: ({ tenant_id, documents }) => ({
        url: '/api/v1/artefacts/upload',
        method: 'POST',
        body: {
          tenant_id,
          site_id: null, // Sites created automatically from asbuilt extraction
          documents,
        },
      }),
    }),
    // Upload callback - notify backend after GCS upload
    uploadCallback: builder.mutation<Document, UploadCallbackRequest>({
      query: (body) => ({
        url: '/api/v1/artefacts/upload/callback',
        method: 'POST',
        body,
      }),
    }),
    // Bulk upload endpoint
    requestBulkUpload: builder.mutation<BulkUploadResponse, { tenant_id: string; documents: BulkUploadDocumentRequest[] }>({
      query: ({ tenant_id, documents }) => ({
        url: '/api/v1/artefacts/upload',
        method: 'POST',
        body: {
          tenant_id,
          site_id: null, // Sites created automatically from asbuilt extraction
          documents,
        },
      }),
    }),
    downloadDocument: builder.query<Blob, string>({
      query: (documentId) => ({
        url: `/api/v1/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    downloadArtefact: builder.mutation<
      { url: string; filename: string; expires_in: number; expires_at: string },
      { artefact_id: string; document_id: string; tenant_id: string; filename: string; expires_in?: number }
    >({
      query: ({ artefact_id, document_id, tenant_id, filename, expires_in = 3600 }) => ({
        url: `/api/v1/artefacts/${artefact_id}/download`,
        method: 'POST',
        body: {
          document_id,
          tenant_id,
          filename,
          expires_in,
        },
      }),
    }),
    exportDocumentExcel: builder.query<Blob, string>({
      query: (documentId) => ({
        url: `/api/v1/documents/${documentId}/export-excel`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    bulkExportExcel: builder.query<Blob, void>({
      query: () => ({
        url: '/api/v1/documents/bulk-export-excel',
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDocumentsQuery,
  useDeleteDocumentMutation,
  useUploadDocumentMutation,
  useRequestUploadMutation,
  useUploadCallbackMutation,
  useRequestBulkUploadMutation,
  useLazyDownloadDocumentQuery,
  useDownloadArtefactMutation,
  useLazyExportDocumentExcelQuery,
  useLazyBulkExportExcelQuery,
} = documentsApi;

